import { get, isFunction, isNil, noop } from "lodash";
import { useCallback, useEffect, useRef, useState } from "react";
import { useMutation } from "shared/hooks/useApi";
import graphql from "utils/api/graphql";
import { API_ERRORS, apiErrorCode } from "../../api-errors";
import { contentKey, draftPayload } from "./draft-content";

export const SAVE_OUTCOMES = {
  SAVED: "SAVED",
  UNCHANGED: "UNCHANGED",
  CONFLICT: "CONFLICT",
  FAILED: "FAILED",
};

const SAVE_DELAY = 800;

const keyOf = (content) => (isNil(content) ? null : contentKey(content));

const useTemplateDraft = ({ template: { id }, onConflict }) => {
  const [saveDraft] = useMutation(
    graphql.mutations.SAVE_DOCUMENT_TEMPLATE_DRAFT,
    { onError: noop },
  );
  const [saveDraftFile] = useMutation(
    graphql.mutations.SAVE_DOCUMENT_TEMPLATE_DRAFT_FILE,
    { onError: noop },
  );
  const [content, setContent] = useState(null);
  const [saved, setSaved] = useState(null);
  const [revision, setRevision] = useState(null);
  const [pendingFile, setPendingFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [failedKey, setFailedKey] = useState(null);
  const [refreshFailed, setRefreshFailed] = useState(false);
  const [conflicted, setConflicted] = useState(false);
  const [holds, setHolds] = useState(0);
  const contentRef = useRef(null);
  const savedRef = useRef(null);
  const revisionRef = useRef(null);
  const holdsRef = useRef(0);
  const conflictedRef = useRef(false);
  const dirtyRef = useRef(false);
  const queue = useRef(Promise.resolve(SAVE_OUTCOMES.UNCHANGED));
  const onConflictRef = useRef(onConflict);
  onConflictRef.current = onConflict;
  contentRef.current = content;

  const hasDraft = !isNil(revision);
  const currentKey = keyOf(content);
  const dirty = !isNil(currentKey) && currentKey !== keyOf(saved);
  const failing = (dirty && failedKey === currentKey) || refreshFailed;
  dirtyRef.current = dirty;

  const reset = useCallback(
    ({ content: next, revision: nextRevision, pendingFile: nextFile }) => {
      contentRef.current = next;
      savedRef.current = next;
      revisionRef.current = nextRevision;
      conflictedRef.current = false;
      setContent(next);
      setSaved(next);
      setRevision(nextRevision);
      setPendingFile(nextFile);
      setFailedKey(null);
      setRefreshFailed(false);
      setConflicted(false);
    },
    [],
  );

  const send = useCallback(
    async (value, { force = false } = {}) => {
      const key = keyOf(value);
      if (!force && key === keyOf(savedRef.current))
        return SAVE_OUTCOMES.UNCHANGED;
      setSaving(true);
      try {
        const result = await saveDraft({
          variables: {
            where: { id },
            data: { content: draftPayload(value) },
            revision: revisionRef.current,
          },
        });
        const row = get(result, "data.saveDocumentTemplateDraft");
        if (isNil(row)) {
          if (apiErrorCode(get(result, "errors")) === API_ERRORS.CONFLICT) {
            conflictedRef.current = true;
            setConflicted(true);
            return SAVE_OUTCOMES.CONFLICT;
          }
          setFailedKey(key);
          return SAVE_OUTCOMES.FAILED;
        }
        savedRef.current = value;
        revisionRef.current = row.revision;
        setSaved(value);
        setRevision(row.revision);
        setPendingFile(get(row, "content.pendingFile") ?? null);
        setFailedKey(null);
        return SAVE_OUTCOMES.SAVED;
      } catch (_error) {
        setFailedKey(key);
        return SAVE_OUTCOMES.FAILED;
      } finally {
        setSaving(false);
      }
    },
    [saveDraft, id],
  );

  const enqueue = useCallback(
    (value, options) => {
      const next = queue.current.then(() => send(value, options));
      queue.current = next;
      return next;
    },
    [send],
  );

  const refresh = useCallback(async () => {
    const refreshed = await Promise.resolve(onConflictRef.current()).catch(
      () => false,
    );
    if (refreshed === false) setRefreshFailed(true);
  }, []);

  useEffect(() => {
    if (!dirty || failing || saving || holds > 0 || conflicted)
      return undefined;
    const timer = setTimeout(async () => {
      if (holdsRef.current > 0 || conflictedRef.current) return;
      const outcome = await enqueue(contentRef.current);
      if (outcome === SAVE_OUTCOMES.CONFLICT) await refresh();
    }, SAVE_DELAY);
    return () => clearTimeout(timer);
  }, [dirty, failing, saving, holds, conflicted, currentKey, enqueue, refresh]);

  useEffect(
    () => () => {
      if (dirtyRef.current && !conflictedRef.current)
        enqueue(contentRef.current);
    },
    [enqueue],
  );

  const update = useCallback(
    (updater) =>
      setContent((previous) =>
        isFunction(updater) ? updater(previous) : updater,
      ),
    [],
  );

  const hold = async (task) => {
    holdsRef.current += 1;
    setHolds((count) => count + 1);
    try {
      await queue.current;
      const result = await task();
      return result;
    } finally {
      holdsRef.current -= 1;
      setHolds((count) => count - 1);
    }
  };

  const persist = (options) => enqueue(contentRef.current, options);

  const retry = () => {
    conflictedRef.current = false;
    setConflicted(false);
    setRefreshFailed(false);
    setFailedKey(null);
  };

  const readRevision = () => revisionRef.current;

  const replaceFile = async ({ file, apply }) => {
    const outcome = await hold(async () => {
      const result = await saveDraftFile({
        variables: { where: { id }, file, revision: revisionRef.current },
      }).catch(() => null);
      const row = get(result, "data.saveDocumentTemplateDraftFile");
      if (isNil(row)) {
        if (apiErrorCode(get(result, "errors")) !== API_ERRORS.CONFLICT)
          return SAVE_OUTCOMES.FAILED;
        conflictedRef.current = true;
        setConflicted(true);
        return SAVE_OUTCOMES.CONFLICT;
      }
      const next = apply(contentRef.current);
      contentRef.current = next;
      savedRef.current = null;
      revisionRef.current = row.revision;
      setContent(next);
      setSaved(null);
      setRevision(row.revision);
      setPendingFile(get(row, "content.pendingFile") ?? null);
      const saved = await send(next, { force: true });
      return saved === SAVE_OUTCOMES.CONFLICT
        ? SAVE_OUTCOMES.CONFLICT
        : SAVE_OUTCOMES.SAVED;
    });
    if (outcome === SAVE_OUTCOMES.CONFLICT) await refresh();
    return outcome;
  };

  return {
    content,
    revision,
    hasDraft,
    pendingFile,
    dirty,
    saving,
    failing,
    reset,
    update,
    hold,
    persist,
    retry,
    refresh,
    readRevision,
    replaceFile,
  };
};

export default useTemplateDraft;
